<template>
  <div class="flex overflow-visible">
    <div :class="showTag && 'flex relative overflow-visible'">
      <img
        v-if="showTag"
        :src="tagBackgroundImage"
        class="h-[150px] w-auto max-w-none"
      >

      <!-- BOGO variant (image-based) -->
      <div
        v-if="variant === 'BOGO'"
        :class="twMerge('flex flex-col items-center relative', showTag && 'absolute top-1/2 -translate-y-1/2 right-[44px]')"
      >
        <img
          :src="DefaultBOGOTagTextImage"
          class="h-[68px] w-auto z-[2]"
        >
        <div
          v-if="!showTag"
          class="absolute left-1/2 top-1/2 w-full h-[80%] -translate-y-1/2 -translate-x-1/2 default-BOGO-shadow z-[1]"
        />
      </div>

      <!-- BOGX variant -->
      <div
        v-else-if="variant === 'BOGX'"
        :class="twMerge('flex flex-col items-center relative -rotate-[16deg] z-[2]', showTag && 'absolute top-1/2 -translate-y-1/2 right-[44px]')"
      >
        <div class="golden-stroke-black-text-6 text-[31px] leading-[31px] font-bold uppercase px-0.5">
          BUY 1
        </div>
        <div
          class="flex items-center text-[18px] leading-[18px] font-bold uppercase -mt-0.5"
          style="text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25)"
        >
          <div class="golden-stroke-black-text px-px">
            GET
          </div>
          <div class="golden-stroke-black-text px-px">
            {{ (amount ?? 2) - 1 }}
          </div>
          <div class="white-stroke-black-text-3 px-0.5">
            FREE
          </div>
        </div>
      </div>

      <!-- discount variant -->
      <div
        v-else-if="variant === 'discount'"
        :class="twMerge('flex flex-col items-center rotate-[-10deg] relative font-[800] uppercase', showTag && 'absolute top-1/2 -translate-y-1/2 right-[50px]')"
      >
        <p :class="twMerge('text-xl leading-[20px] z-[2] default-discount-tag-text overflow-visible px-0.5')">
          {{ `${Math.trunc(saleValue*100)}% off` }}
        </p>
        <p :class="twMerge('text-[15px] leading-[16px] tracking-[0.4px] z-[2] default-discount-tag-text px-0.5')">
          EVERYTHING
        </p>
        <div
          v-if="!showTag"
          class="absolute left-1/2 top-1/2 w-full h-[80%] -translate-y-1/2 -translate-x-1/2 default-discount-shadow z-[1]"
        />
      </div>

      <!-- cashback variant -->
      <div
        v-else-if="variant === 'cashback'"
        :class="twMerge('flex flex-col items-center rotate-[-10deg] relative font-[800] uppercase', showTag && 'absolute top-1/2 -translate-y-1/2 right-[50px]')"
      >
        <p :class="twMerge('text-xl leading-[20px] z-[2] default-cashback-tag-text overflow-visible px-0.5')">
          get {{ `${Math.trunc(saleValue*100)}% ` }}
        </p>
        <p :class="twMerge('text-xl leading-[20px] z-[2] default-cashback-tag-text px-0.5')">
          in <span class="default-cashback-tag-text-secondary"> stars</span>
        </p>
        <div
          v-if="!showTag"
          class="absolute left-1/2 top-1/2 w-full h-[80%] -translate-y-1/2 -translate-x-1/2 default-cashback-shadow z-[1]"
        />
      </div>

      <!-- IWD variant -->
      <div
        v-else-if="variant === 'IWD'"
        :class="twMerge('flex flex-col items-center rotate-[-10deg] relative font-[800] uppercase', showTag && 'absolute top-1/2 -translate-y-1/2 right-[50px]')"
      >
        <p :class="twMerge('text-xl leading-[20px] z-[2] IWD-tag-text overflow-visible px-0.5')">
          {{ `${Math.trunc(saleValue*100)}% off` }}
        </p>
        <p :class="twMerge('text-[15px] leading-[16px] tracking-[0.4px] z-[2] IWD-tag-text px-0.5')">
          EVERYTHING
        </p>
        <div
          v-if="!showTag"
          class="absolute left-1/2 top-1/2 w-full h-[80%] -translate-y-1/2 -translate-x-1/2 IWD-shadow z-[1]"
        />
      </div>

      <!-- STP variant -->
      <div
        v-else-if="variant === 'STP'"
        :class="twMerge('flex flex-col items-center rotate-[-10deg] relative font-[800] uppercase', showTag && 'absolute top-1/2 -translate-y-1/2 right-[50px]')"
      >
        <p :class="twMerge('text-xl leading-[20px] z-[2] IWD-tag-text overflow-visible px-0.5')">
          {{ `${Math.trunc(saleValue * 100)}% off` }}
        </p>
        <p :class="twMerge('text-[15px] leading-[16px] tracking-[0.4px] z-[2] IWD-tag-text px-0.5')">
          EVERYTHING
        </p>
        <div
          v-if="!showTag"
          class="absolute left-1/2 top-1/2 w-full h-[80%] -translate-y-1/2 -translate-x-1/2 STP-shadow z-[1]"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { twMerge } from 'tailwind-merge'
import { useShop } from '@/composables/game/shop'

// Import all tag background images
import DefaultBOGOTagImage from '@/assets/images/periodic/default-BOGO/default-BOGO-tag.png'
import DefaultBOGOTagTextImage from '@/assets/images/periodic/default-BOGO/default-BOGO-tag-text.png'
import DefaultBOGXTagImage from '@/assets/images/periodic/default-BOGX/default-BOGX-tag.png'
import DefaultDiscountTagImage from '@/assets/images/periodic/default-discount/default-discount-tag.png'
import DefaultCashbackTagImage from '@/assets/images/periodic/default-cashback/default-cashback-tag.png'
import IWDTagImage from '@/assets/images/periodic/IWD/IWD-tag.png'
import STPTagImage from '@/assets/images/periodic/STP/STP-tag.png'

export type TagVariant = 'BOGO' | 'BOGX' | 'discount' | 'cashback' | 'IWD' | 'STP'

const props = defineProps<{
  variant: TagVariant
  showTag?: boolean
  amount?: number  // Required for BOGX variant
}>()

const { saleValue } = useShop()

const tagBackgroundImage = computed(() => {
  const imageMap: Record<TagVariant, string> = {
    'BOGO': DefaultBOGOTagImage,
    'BOGX': DefaultBOGXTagImage,
    'discount': DefaultDiscountTagImage,
    'cashback': DefaultCashbackTagImage,
    'IWD': IWDTagImage,
    'STP': STPTagImage,
  }
  return imageMap[props.variant]
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/sales';
</style>
